import React, { useState, useEffect } from "react";
import styles from "./budget-line-edit-modal.module.css";
import FormSection from "../../screens/form-section";
import Button from "../../ui/button";
import Input from "../../ui/input";
import Dropdown from "../../ui/dropdown";
import Checkbox from "../../ui/checkbox";
import { MyIcons } from "../../icons/my-icons";
import { getBudgetLineById, editBudgetLine, getGuilds, getDefaultBudgets, getRConjuntos } from "../../../services/apiService";
import { showSuccessToast, showErrorToast } from "../../../utils/notifications";

const BudgetLineEditModal = ({ isOpen, onClose, lineaId, expedienteId, onUpdate }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [guildOptions, setGuildOptions] = useState([]);
  const [defaultBudgetOptions, setDefaultBudgetOptions] = useState([]);
  const [rConjuntosData, setRConjuntosData] = useState(null);
  
  const [formData, setFormData] = useState({
    guild: null,
    defaultBudget: null,
    budgetNumber: "",
    // Costes fields
    workforce: "",
    workerMaterial: "",
    companyMaterial: "",
    rebateWorkforce: "",
    rebateWorkerMaterial: "",
    quantity: "",
    // Valores presupuesto
    budgetPrice: "",
    materialPrice: "",
    totalCost: "",
    totalBudget: "",
    totalRConjunto: "",
    totalRentabilityPercentage: "",
    totalRentability: "",
    information: "",
    // Campos para rangos
    rConjuntoMin: "",
    rConjuntoMax: "",
    priceMin: "",
    priceMax: "",
    rentabilityMin: "",
    rentabilityMax: "",
    workforcePriceMin: "",
    workforcePriceMax: "",
    materialPriceMin: "",
    materialPriceMax: "",
    materialUse: "",
  });

  const [valuesMode, setValuesMode] = useState({
    value: "normal",
    label: "Normal"
  });
  const [costMode, setCostMode] = useState({
    value: "normal",
    label: "Normal"
  });
  const [isDone, setIsDone] = useState(false);
  const [doNotDo, setDoNotDo] = useState(false);

  // Read-only mode for this modal: prevent any edits
  const readOnlyMode = true;

  const costModeOptions = [
    { value: "normal", label: "Normal" },
    { value: "rebate", label: "Rebajado" },
    { value: "quantity", label: "Cantidad" },
  ];

  const valuesModeOptions = [
    { value: "normal", label: "Normal" },
    { value: "benefit", label: "Rentabilidad" },
    { value: "detailed", label: "Desglosado" },
  ];

  useEffect(() => {
    if (!isOpen || !lineaId || !expedienteId) return;

    let isMounted = true;

    const loadAllData = async () => {
      setLoading(true);
      try {
        // Cargar opciones de dropdown primero
        const [guilds, defaultBudgets, rConjuntos, details] = await Promise.all([
          getGuilds(),
          getDefaultBudgets(),
          getRConjuntos(expedienteId),
          getBudgetLineById(lineaId)
        ]);

        setRConjuntosData(rConjuntos);

        if (!isMounted) return;

        console.log("Budget line details loaded:", details);

        // Configurar opciones de dropdown
        const guildOpts = guilds.map((item) => ({ value: item.id, label: item.nombre }));
        const budgetOpts = defaultBudgets.map((item) => ({ value: item.id, label: item.nombre }));
        setGuildOptions(guildOpts);
        setDefaultBudgetOptions(budgetOpts);

        // Encontrar guild y budget seleccionados
        const selectedGuild = guildOpts.find(guild => guild.value === details.gremioId);
        const selectedDefaultBudget = budgetOpts.find(budget => budget.value === details.ptoPredId);

        // Mapear tipo de coste
        const costModeMap = {
          "Normal": { value: "normal", label: "Normal" },
          "Rebajado": { value: "rebate", label: "Rebajado" },
          "Cantidad": { value: "quantity", label: "Cantidad" }
        };
        const selectedCostMode = costModeMap[details.tipoCoste] || { value: "normal", label: "Normal" };

        // Mapear tipo de valores
        const valuesModeMap = {
          "Normal": { value: "normal", label: "Normal" },
          "Desglosado": { value: "detailed", label: "Desglosado" },
          "Rentabilidad": { value: "benefit", label: "Rentabilidad" }
        };
        const selectedValuesMode = valuesModeMap[details.tipoValores] || { value: "normal", label: "Normal" };
        
        console.log("tipoValores from backend:", details.tipoValores);
        console.log("selectedValuesMode:", selectedValuesMode);

        // Configurar todo el estado de una vez
        setFormData({
          guild: selectedGuild || null,
          defaultBudget: selectedDefaultBudget || null,
          budgetNumber: details.numPresupuesto?.toString() || "",
          workforce: details.costeManoObraOperario?.toString() || "",
          workerMaterial: details.costeMaterialOperario?.toString() || "",
          companyMaterial: details.costeMaterialEmpresa?.toString() || "",
          rebateWorkforce: details.costeManoObraOperarioRebajado?.toString() || "",
          rebateWorkerMaterial: details.costeMaterialOperarioRebajado?.toString() || "",
          quantity: details.cantidad?.toString() || "",
          budgetPrice: details.presupuestoManoObra?.toString() || "",
          materialPrice: details.presupuestoMaterial?.toString() || "",
          totalCost: details.costeTotal?.toString() || "",
          totalBudget: details.presupuestoTotal?.toString() || "",
          totalRConjunto: details.conjuntoTotal?.toString() || "",
          totalRentabilityPercentage: details.porcentageBeneficioTotal?.toString() || "",
          totalRentability: details.beneficioTotal?.toString() || "",
          information: details.infoTrabajo || "",
          rConjuntoMin: details.rconjuntoMinimo?.toString() || "",
          rConjuntoMax: details.rconjuntoMaximo?.toString() || "",
          priceMin: details.precioMinimo?.toString() || "",
          priceMax: details.precioMaximo?.toString() || "",
          rentabilityMin: details.porcentageBeneficioMinimo?.toString() || "",
          rentabilityMax: details.porcentageBeneficioMaximo?.toString() || "",
          workforcePriceMin: "",
          workforcePriceMax: "",
          materialPriceMin: details.precioMinimoMaterial?.toString() || "",
          materialPriceMax: details.precioMaximoMaterial?.toString() || "",
          materialUse: details.infoMaterial || "",
        });

        setCostMode(selectedCostMode);
        setValuesMode(selectedValuesMode);
        console.log("After setting valuesMode state:", selectedValuesMode);
        setIsDone(details.realizado || false);
        setDoNotDo(details.noRealizar || false);

        // Si no hay rConjuntos en los detalles, usar los de la API
        if (rConjuntos && rConjuntos.length >= 3 && (!details.rconjuntoMinimo || !details.rconjuntoMaximo)) {
          const [, rConjuntoMin, rConjuntoMax] = rConjuntos;
          setFormData(prev => ({
            ...prev,
            rConjuntoMin: prev.rConjuntoMin || rConjuntoMin.toFixed(3),
            rConjuntoMax: prev.rConjuntoMax || rConjuntoMax.toFixed(3),
          }));
        }

      } catch (error) {
        console.error("Error loading budget line:", error);
        if (isMounted) {
          showErrorToast("Error al cargar la línea de presupuesto");
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadAllData();

    return () => {
      isMounted = false;
    };
  }, [isOpen, lineaId, expedienteId]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleDropdownSelect = (field, option) => {
    setFormData({ ...formData, [field]: option });
  };

  const handleCostModeChange = (option) => {
    setCostMode(option);
  };

  const handleValuesModeChange = (option) => {
    setValuesMode(option);
  };

  const handleIsDoneChange = (checked) => {
    setIsDone(checked);
    if (checked) setDoNotDo(false);
  };

  const handleDoNotDoChange = (checked) => {
    setDoNotDo(checked);
    if (checked) setIsDone(false);
  };

  // Funciones de cálculo (replicadas de budget-lines)
  const calculateTotalCosts = (workforce, workerMaterial, companyMaterial, quantity = 1) => {
    const workforceVal = parseFloat(workforce) || 0;
    const workerMaterialVal = parseFloat(workerMaterial) || 0;
    const companyMaterialVal = parseFloat(companyMaterial) || 0;
    const quantityVal = parseFloat(quantity) || 1;
    
    return (workforceVal + workerMaterialVal + companyMaterialVal) * quantityVal;
  };

  const calculatePricesFromRConjunto = (totalCosts, rConjuntoMin, rConjuntoMax) => {
    if (!totalCosts || !rConjuntoMin || !rConjuntoMax) return { priceMin: 0, priceMax: 0 };
    
    const priceMin = totalCosts / rConjuntoMin;
    const priceMax = totalCosts / rConjuntoMax;

    return { priceMin, priceMax };
  };

  const calculatePricesFromRentability = (totalCosts, adminCommission = 10) => {
    if (!totalCosts) return { priceMin: 0, priceMax: 0 };
    
    const X = totalCosts;
    const Y_min = 25;
    const Y_max = 35;
    const Z = adminCommission;
    
    const priceMin = (X * (1 + Y_min / 100)) / (1 - Z / 100);
    const priceMax = (X * (1 + Y_max / 100)) / (1 - Z / 100);
    
    return { priceMin, priceMax };
  };

  const calculateRentabilityPercentage = (budgetPrice, totalCosts, adminCommission = 10) => {
    if (!budgetPrice || !totalCosts || budgetPrice === 0 || totalCosts === 0) return 0;
    
    const P = parseFloat(budgetPrice);
    const X = parseFloat(totalCosts);
    const Z = adminCommission;
    
    const rentabilityPercentage = 100 * ((P * (1 - Z / 100)) / X - 1);
    return rentabilityPercentage;
  };

  const calculateRentability = (rentabilityPercentage, totalCosts) => {
    if (!rentabilityPercentage || !totalCosts) return 0;
    
    const rentability = (parseFloat(rentabilityPercentage) * parseFloat(totalCosts)) / 100;
    return rentability;
  };

  const calculateRConjunto = (totalCosts, budgetPrice) => {
    if (!totalCosts || !budgetPrice || budgetPrice === 0) return 0;
    
    return totalCosts / budgetPrice;
  };

  // Efecto para calcular automáticamente cuando cambian los valores
  useEffect(() => {
    const workforceVal = parseFloat(formData.workforce) || 0;
    const workerMaterialVal = parseFloat(formData.workerMaterial) || 0;
    const companyMaterialVal = parseFloat(formData.companyMaterial) || 0;
    const quantityVal = parseFloat(formData.quantity) || 1;
    const budgetPriceVal = parseFloat(formData.budgetPrice) || 0;
    const materialPriceVal = parseFloat(formData.materialPrice) || 0;
    
    // Calcular costes totales
    const totalCosts = calculateTotalCosts(formData.workforce, formData.workerMaterial, formData.companyMaterial, formData.quantity);
    
    // Calcular presupuesto total
    let totalBudget = budgetPriceVal;
    if (valuesMode.value === "detailed" && materialPriceVal) {
      totalBudget += materialPriceVal;
    }
    
    // Calcular rConjunto
    const rConjunto = calculateRConjunto(totalCosts, totalBudget);
    
    // Calcular porcentaje de rentabilidad
    const adminCommission = rConjuntosData ? rConjuntosData[0] : 10;
    const rentabilityPercentage = calculateRentabilityPercentage(totalBudget, totalCosts, adminCommission);
    
    // Calcular rentabilidad en euros
    const rentability = calculateRentability(rentabilityPercentage, totalCosts);
    
    // Calcular precios según el modo de valores
    let priceMin = 0, priceMax = 0;
    let workforcePriceMin = 0, workforcePriceMax = 0;
    let materialPriceMin = 0, materialPriceMax = 0;
    
    if (rConjuntosData && totalCosts > 0) {
      if (valuesMode.value === "normal" && formData.rConjuntoMin && formData.rConjuntoMax) {
        const prices = calculatePricesFromRConjunto(totalCosts, parseFloat(formData.rConjuntoMin), parseFloat(formData.rConjuntoMax));
        priceMin = prices.priceMin;
        priceMax = prices.priceMax;
      } else if (valuesMode.value === "benefit") {
        const prices = calculatePricesFromRentability(totalCosts, adminCommission);
        priceMin = prices.priceMin;
        priceMax = prices.priceMax;
      } else if (valuesMode.value === "detailed" && formData.rConjuntoMin && formData.rConjuntoMax) {
        const workforceCost = workforceVal * quantityVal;
        const materialCost = (workerMaterialVal + companyMaterialVal) * quantityVal;
        
        const workforcePrices = calculatePricesFromRConjunto(workforceCost, parseFloat(formData.rConjuntoMin), parseFloat(formData.rConjuntoMax));
        const materialPrices = calculatePricesFromRConjunto(materialCost, parseFloat(formData.rConjuntoMin), parseFloat(formData.rConjuntoMax));
        
        workforcePriceMin = workforcePrices.priceMin;
        workforcePriceMax = workforcePrices.priceMax;
        materialPriceMin = materialPrices.priceMin;
        materialPriceMax = materialPrices.priceMax;
      }
    }
    
    // Actualizar campos calculados
    setFormData(prev => ({
      ...prev,
      totalCost: totalCosts.toFixed(2),
      totalBudget: totalBudget.toFixed(2),
      totalRConjunto: rConjunto.toFixed(4),
      totalRentabilityPercentage: rentabilityPercentage.toFixed(2),
      totalRentability: rentability.toFixed(2),
      priceMin: priceMin.toFixed(2),
      priceMax: priceMax.toFixed(2),
      workforcePriceMin: workforcePriceMin.toFixed(2),
      workforcePriceMax: workforcePriceMax.toFixed(2),
      materialPriceMin: materialPriceMin.toFixed(2),
      materialPriceMax: materialPriceMax.toFixed(2),
    }));
    
  }, [
    formData.workforce, 
    formData.workerMaterial, 
    formData.companyMaterial, 
    formData.quantity,
    formData.budgetPrice, 
    formData.materialPrice,
    formData.rConjuntoMin,
    formData.rConjuntoMax,
    valuesMode.value,
    rConjuntosData
  ]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const budgetLineData = {
        gremioId: formData.guild?.value || null,
        ptoPredId: formData.defaultBudget?.value || null,
        expedienteId: parseInt(expedienteId),
        numPresupuesto: parseInt(formData.budgetNumber) || null,
        realizado: isDone,
        noRealizar: doNotDo,
        costeManoObraOperario: parseFloat(formData.workforce) || 0.0,
        costeMaterialOperario: parseFloat(formData.workerMaterial) || 0.0,
        costeMaterialEmpresa: parseFloat(formData.companyMaterial) || 0.0,
        tipoCoste: costMode?.label || "Normal",
        costeManoObraOperarioRebajado: parseFloat(formData.rebateWorkforce) || 0.0,
        costeMaterialOperarioRebajado: parseFloat(formData.rebateWorkerMaterial) || 0.0,
        cantidad: parseFloat(formData.quantity) || 1,
        presupuestoManoObra: parseFloat(formData.budgetPrice) || 0.0,
        presupuestoMaterial: parseFloat(formData.materialPrice) || 0.0,
        tipoValores: valuesMode?.label || "Normal",
        rConjuntoMinimo: parseFloat(formData.priceMin) || 0.0,
        rConjuntoMaximo: parseFloat(formData.priceMax) || 0.0,
        porcentageBeneficioMinimo: parseFloat(formData.workforcePriceMin) || 0.0,
        porcentageBeneficioMaximo: parseFloat(formData.workforcePriceMax) || 0.0,
        precioMinimo: parseFloat(formData.materialPriceMin) || 0.0,
        precioMaximo: parseFloat(formData.materialPriceMax) || 0.0,
        precioMinimoMaterial: parseFloat(formData.materialPriceMin) || 0.0,
        precioMaximoMaterial: parseFloat(formData.materialPriceMax) || 0.0,
        costeTotal: parseFloat(formData.totalCost) || 0.0,
        presupuestoTotal: parseFloat(formData.totalBudget) || 0.0,
        conjuntoTotal: parseFloat(formData.totalRConjunto) || 0.0,
        porcentageBeneficioTotal: parseFloat(formData.totalRentabilityPercentage) || 0.0,
        beneficioTotal: parseFloat(formData.totalRentability) || 0.0,
        infoTrabajo: formData.information || "",
        infoMaterial: formData.materialUse || ""
      };

      console.log("Saving budget line:", budgetLineData);
      await editBudgetLine(lineaId, budgetLineData);
      showSuccessToast("Línea de presupuesto actualizada exitosamente");
      
      if (onUpdate) {
        onUpdate();
      }
      
      onClose();
    } catch (error) {
      console.error("Error saving budget line:", error);
      showErrorToast("Error al actualizar la línea de presupuesto");
    } finally {
      setSaving(false);
    }
  };

  const renderValuesSection = () => {
    console.log("renderValuesSection called with valuesMode:", valuesMode);
    switch (valuesMode.value) {
      case "normal":
        return (
          <div className={styles.valuesContainer}>
            <div className={styles.inputRow}>
              <Input
                label="rConjunto mínimo"
                name="rConjuntoMin"
                placeholder="rConjunto"
                value={formData.rConjuntoMin}
                onChange={handleInputChange}
                readOnly={readOnlyMode}
                disabled={readOnlyMode}
                style={{ flex: 1 }}
              />
              <Input
                label="rConjunto máximo"
                name="rConjuntoMax"
                placeholder="rConjunto"
                value={formData.rConjuntoMax}
                onChange={handleInputChange}
                readOnly={readOnlyMode}
                disabled={readOnlyMode}
                style={{ flex: 1 }}
              />
            </div>
            <div className={styles.inputRow}>
              <Input
                label="Precio mínimo"
                name="priceMin"
                placeholder="Precio"
                value={formData.priceMin}
                onChange={handleInputChange}
                readOnly={readOnlyMode}
                disabled={readOnlyMode}
                style={{ flex: 1 }}
              />
              <Input
                label="Precio máximo"
                name="priceMax"
                placeholder="Precio"
                value={formData.priceMax}
                onChange={handleInputChange}
                readOnly={readOnlyMode}
                disabled={readOnlyMode}
                style={{ flex: 1 }}
              />
            </div>
          </div>
        );
      case "benefit":
        return (
          <div className={styles.valuesContainer}>
            <div className={styles.inputRow}>
              <Input
                label="Rentabilidad mínima (%)"
                name="rentabilityMin"
                value="25"
                readOnly
                style={{ flex: 1 }}
              />
              <Input
                label="Rentabilidad máxima (%)"
                name="rentabilityMax"
                value="35"
                readOnly
                style={{ flex: 1 }}
              />
            </div>
            <div className={styles.inputRow}>
              <Input
                label="Precio mínimo"
                name="priceMin"
                placeholder="Precio"
                value={formData.priceMin}
                onChange={handleInputChange}
                readOnly={readOnlyMode}
                disabled={readOnlyMode}
                style={{ flex: 1 }}
              />
              <Input
                label="Precio máximo"
                name="priceMax"
                placeholder="Precio"
                value={formData.priceMax}
                onChange={handleInputChange}
                readOnly={readOnlyMode}
                disabled={readOnlyMode}
                style={{ flex: 1 }}
              />
            </div>
          </div>
        );
      case "detailed":
        return (
          <div className={styles.valuesContainer}>
            <div className={styles.inputRow}>
              <Input
                label="rConjunto mínimo"
                name="rConjuntoMin"
                placeholder="rConjunto"
                value={formData.rConjuntoMin}
                onChange={handleInputChange}
                readOnly={readOnlyMode}
                disabled={readOnlyMode}
                style={{ flex: 1 }}
              />
              <Input
                label="rConjunto máximo"
                name="rConjuntoMax"
                placeholder="rConjunto"
                value={formData.rConjuntoMax}
                onChange={handleInputChange}
                readOnly={readOnlyMode}
                disabled={readOnlyMode}
                style={{ flex: 1 }}
              />
            </div>
            <div className={styles.inputRow}>
              <Input
                label="Precio mano obra mín"
                name="workforcePriceMin"
                placeholder="Precio"
                value={formData.workforcePriceMin}
                onChange={handleInputChange}
                readOnly={readOnlyMode}
                disabled={readOnlyMode}
                style={{ flex: 1 }}
              />
              <Input
                label="Precio mano obra máx"
                name="workforcePriceMax"
                placeholder="Precio"
                value={formData.workforcePriceMax}
                onChange={handleInputChange}
                readOnly={readOnlyMode}
                disabled={readOnlyMode}
                style={{ flex: 1 }}
              />
            </div>
            <div className={styles.inputRow}>
              <Input
                label="Precio material mín"
                name="materialPriceMin"
                placeholder="Precio"
                value={formData.materialPriceMin}
                onChange={handleInputChange}
                readOnly={readOnlyMode}
                disabled={readOnlyMode}
                style={{ flex: 1 }}
              />
              <Input
                label="Precio material máx"
                name="materialPriceMax"
                placeholder="Precio"
                value={formData.materialPriceMax}
                onChange={handleInputChange}
                readOnly={readOnlyMode}
                disabled={readOnlyMode}
                style={{ flex: 1 }}
              />
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  const handleClose = () => {
    // Resetear el formulario al cerrar
    setFormData({
      guild: null,
      defaultBudget: null,
      budgetNumber: "",
      workforce: "",
      workerMaterial: "",
      companyMaterial: "",
      quantity: "",
      rebateWorkforce: "",
      rebateWorkerMaterial: "",
      budgetPrice: "",
      materialPrice: "",
      rConjuntoMin: "",
      rConjuntoMax: "",
      totalCost: "",
      totalBudget: "",
      totalRConjunto: "",
      totalRentabilityPercentage: "",
      totalRentability: "",
      priceMin: "",
      priceMax: "",
      workforcePriceMin: "",
      workforcePriceMax: "",
      materialPriceMin: "",
      materialPriceMax: "",
      information: "",
      materialUse: "",
    });
    setIsDone(false);
    setDoNotDo(false);
    setCostMode(costModeOptions[0]);
    setValuesMode(valuesModeOptions[0]);
    onClose();
  };

  if (!isOpen) return null;

  if (loading) {
    return (
      <div className={styles.modalOverlay}>
        <div className={styles.modalContentWrapper}>
          <div className={styles.modalHeader}>
            <h2 className={styles.modalTitle}>Detalle Línea de Presupuesto</h2>
            <button className={styles.closeButton} onClick={handleClose}>
              <MyIcons name="close" size={24} color="black" />
            </button>
          </div>
          <div className={styles.modalBody}>
            <div className={styles.loadingContainer}>
              <p>Cargando información...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.modalOverlay} onClick={handleClose}>
      <div className={styles.modalContentWrapper} onClick={(e) => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <h2 className={styles.modalTitle}>Visualizar Línea de Presupuesto</h2>
          <button className={styles.closeButton} onClick={handleClose}>
            <MyIcons name="close" size={24} color="black" />
          </button>
        </div>
        <div className={styles.modalBody}>
          <FormSection
          title="Trabajos a realizar"
          style={{
            flex: 1,
            display: "flex",
            minWidth: "400px",
            flexDirection: "column",
            width: "100%",
          }}
        >
          <textarea
            name="information"
            placeholder="Trabajos que se van a realizar"
            value={formData.information}
            onChange={handleInputChange}
            readOnly={readOnlyMode}
            style={{
              width: "100%",
              height: "200px",
              paddingTop: "12px",
              paddingBottom: "12px",
              paddingLeft: "12px",
              paddingRight: "12px",
              border: "1px solid #ddd",
              borderRadius: "4px",
              fontFamily: "var(--font-roboto)",
              fontSize: "14px",
              color: "black",
              resize: "none",
              outline: "none",
              whiteSpace: "pre-wrap",
              wordWrap: "break-word",
              overflow: "auto",
            }}
            spellCheck="true"
          />
        </FormSection>
        <FormSection
          title="General"
          renderRight={() => (
            <div className={styles.checkboxContainer}>
              <Checkbox
                label="¿Realizado?"
                checked={isDone}
                onChange={handleIsDoneChange}
                disabled={readOnlyMode}
              />
              <Checkbox
                label="No realizar"
                checked={doNotDo}
                onChange={handleDoNotDoChange}
                disabled={readOnlyMode}
              />
            </div>
          )}
        >
          <div className={styles.inputRow}>
            <Dropdown
              key={`guild-${lineaId}-${formData.guild?.value || 'none'}`}
              label="Gremio"
              options={guildOptions}
              onSelect={(option) => handleDropdownSelect("guild", option)}
              disabled={readOnlyMode}
              selected={formData.guild}
              style={{ marginTop: "3px", width: "100%" }}
              mainStyle={{ flex: 1 }}
            />
            <Dropdown
              key={`budget-${lineaId}-${formData.defaultBudget?.value || 'none'}`}
              label="Pto. Predeterminado"
              options={defaultBudgetOptions}
              onSelect={(option) => handleDropdownSelect("defaultBudget", option)}
              disabled={readOnlyMode}
              selected={formData.defaultBudget}
              clearable
              style={{ marginTop: "3px", width: "100%" }}
              mainStyle={{ flex: 1 }}
            />
            <Input
              label="Nº de presupuesto"
              name="budgetNumber"
              placeholder="Numero"
              value={formData.budgetNumber}
              onChange={handleInputChange}
              readOnly={readOnlyMode}
              disabled={readOnlyMode}
              type="number"
              style={{ flex: 1 }}
            />
          </div>
        </FormSection>

        <FormSection
          title="Costes"
          renderRight={() => (
            <Dropdown
              key={`cost-mode-${lineaId}-${costMode.value}`}
              options={costModeOptions}
              onSelect={handleCostModeChange}
              selected={costMode}
              clearable={false}
              style={{ height: "25px" }}
              mainStyle={{ flex: 1 }}
              disabled={readOnlyMode}
            />
          )}
        >
          <div className={styles.inputRow}>
            <Input
              label="Mano de obra"
              name="workforce"
              placeholder="Coste"
              value={formData.workforce}
              onChange={handleInputChange}
              readOnly={readOnlyMode}
              disabled={readOnlyMode}
              style={{ flex: 1 }}
            />
            <Input
              label="Material operario"
              name="workerMaterial"
              placeholder="Coste"
              value={formData.workerMaterial}
              onChange={handleInputChange}
              readOnly={readOnlyMode}
              disabled={readOnlyMode}
              style={{ flex: 1 }}
            />
            <Input
              label="Material empresa"
              name="companyMaterial"
              placeholder="Coste"
              value={formData.companyMaterial}
              onChange={handleInputChange}
              readOnly={readOnlyMode}
              disabled={readOnlyMode}
              style={{ flex: 1 }}
            />
          </div>

          {costMode.value === "rebate" && (
            <div className={styles.inputRow}>
              <Input
                label="Rebajado mano obra"
                name="rebateWorkforce"
                placeholder="Rebaja"
                value={formData.rebateWorkforce}
                onChange={handleInputChange}
                readOnly={readOnlyMode}
                disabled={readOnlyMode}
                style={{ flex: 1 }}
              />
              <Input
                label="Rebajado material operario"
                name="rebateWorkerMaterial"
                placeholder="Rebaja"
                value={formData.rebateWorkerMaterial}
                onChange={handleInputChange}
                readOnly={readOnlyMode}
                disabled={readOnlyMode}
                style={{ flex: 1 }}
              />
            </div>
          )}

          {costMode.value === "quantity" && (
            <div className={styles.inputRow}>
              <Input
                label="Cantidad"
                name="quantity"
                placeholder="Cantidad"
                value={formData.quantity}
                onChange={handleInputChange}
                readOnly={readOnlyMode}
                disabled={readOnlyMode}
                style={{ flex: 1 }}
              />
            </div>
          )}
        </FormSection>

        <FormSection title="Valores presupuesto">
          <div className={styles.inputRow}>
            <Input
              label="Precio presupuesto"
              name="budgetPrice"
              placeholder="Precio"
              value={formData.budgetPrice}
              onChange={handleInputChange}
              readOnly={readOnlyMode}
              disabled={readOnlyMode}
              style={{ flex: 1 }}
            />
            {valuesMode.value === "detailed" && (
              <Input
                label="Material"
                name="materialPrice"
                placeholder="Precio"
                value={formData.materialPrice}
                onChange={handleInputChange}
                readOnly={readOnlyMode}
                disabled={readOnlyMode}
                style={{ flex: 1 }}
              />
            )}
          </div>
        </FormSection>

        <FormSection
          title="Valores"
          renderRight={() => (
            <Dropdown
              key={`values-mode-${lineaId}-${valuesMode.value}`}
              options={valuesModeOptions}
              onSelect={handleValuesModeChange}
              disabled={readOnlyMode}
              selected={valuesMode}
              clearable={false}
              style={{ height: "25px" }}
              mainStyle={{ flex: 1 }}
            />
          )}
        >
          {renderValuesSection()}
        </FormSection>

        <FormSection title="Totales">
          <div className={styles.inputRow}>
            <Input
              label="Coste"
              name="totalCost"
              placeholder="Precio"
              value={formData.totalCost}
              style={{ flex: 1 }}
              readOnly
            />
            <Input
              label="Presupuesto"
              name="totalBudget"
              placeholder="Precio"
              value={formData.totalBudget}
              style={{ flex: 1 }}
              readOnly
            />
            <Input
              label="R. conjunto"
              name="totalRConjunto"
              placeholder="rConjunto"
              value={formData.totalRConjunto}
              style={{ flex: 1 }}
              readOnly
            />
          </div>
          <div className={styles.inputRow}>
            <Input
              label="Porcentaje de rentabilidad"
              name="totalRentabilityPercentage"
              placeholder="Porcentaje"
              value={formData.totalRentabilityPercentage}
              style={{ flex: 1 }}
              readOnly
            />
            <Input
              label="Rentabilidad"
              name="totalRentability"
              placeholder="Euros"
              value={formData.totalRentability}
              style={{ flex: 1 }}
              readOnly
            />
          </div>
        </FormSection>

        
        {valuesMode?.value === "detailed" && (
          <FormSection
            title="Material"
            style={{
              flex: 1,
              display: "flex",
              minWidth: "400px",
              flexDirection: "column",
              width: "100%",
            }}
          >
            <textarea
              name="materialUse"
              placeholder="Materiales que se usarán"
              value={formData.materialUse}
              onChange={handleInputChange}
              readOnly={readOnlyMode}
              style={{
                width: "100%",
                height: "80px",
                paddingTop: "12px",
                paddingBottom: "12px",
                paddingLeft: "12px",
                paddingRight: "12px",
                border: "1px solid #ddd",
                borderRadius: "4px",
                fontFamily: "var(--font-roboto)",
                fontSize: "14px",
                color: "black",
                resize: "none",
                outline: "none",
                whiteSpace: "pre-wrap",
                wordWrap: "break-word",
                overflow: "auto",
              }}
              spellCheck="true"
            />
          </FormSection>
        )}

          {/*<div className={styles.modalFooter}>
            <Button variant="secondary" onClick={handleClose}>
              Cancelar
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? "Guardando..." : "Guardar Cambios"}
            </Button>
          </div>*/}
        </div>
      </div>
    </div>
  );
};

export default BudgetLineEditModal;
